import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth0Context } from '../contexts/Auth0Context';
import { useAuthToken } from '../utils/authTokenManager';
import { PROCOGIA_COLORS } from '../utils/themeUtils';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  Search, 
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Calendar,
  Filter,
  RefreshCw
} from 'lucide-react';

const AlertsPage = () => {
  const { getThemeClasses } = useTheme();
  const { appUser, getAccessTokenSilently } = useAuth0Context();
  const themeClasses = getThemeClasses();
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null); // New state for error messages

  // Removed: Now handled by the alertsAuthToken useEffect above

  useEffect(() => {
    filterAlerts();
  }, [alerts, searchTerm, severityFilter, statusFilter]);

  // Use centralized token manager (no hook violations)
  const alertsAuthToken = useAuthToken(appUser, getAccessTokenSilently);

  // Fetch alerts when token is available
  useEffect(() => {
    if (appUser && alertsAuthToken) {
      fetchAlerts(true, alertsAuthToken); // Force cache-busting on initial load
    }
  }, [appUser, alertsAuthToken]);

  // Wrapper function that uses stored token (no hook violations)
  const handleFetchAlerts = async (bustCache = false) => {
    if (!alertsAuthToken) {
      setError('Authentication token not available. Please refresh and try again.');
      return;
    }

    try {
      await fetchAlerts(bustCache, alertsAuthToken);
    } catch (error) {
      console.error('❌ [DEBUG] Error fetching alerts:', error);
      setLoading(false);
      setError(error.message || 'Failed to fetch alerts. Please try again.');
    }
  };

  const fetchAlerts = async (bustCache = false, token = null) => {
    try {
      setLoading(true);
      
      console.log('🔄 [DEBUG] Starting alerts fetch process...', {
        bustCache,
        timestamp: new Date().toISOString(),
        appUser: appUser ? { id: appUser.id, role: appUser.role } : 'NULL',
        component: 'AlertsPage'
      });
      
      // Use provided token
      if (!token) {
        console.error('❌ No auth token provided to fetchAlerts');
        throw new Error('Authentication token required');
      }
      
      console.log('✅ [DEBUG] Auth0 token acquired successfully', { 
        tokenLength: token?.length || 0,
        tokenPrefix: token?.substring(0, 20) + '...',
        tokenType: typeof token
      });
      
      console.log('🔄 [DEBUG] Starting alerts fetch with token...', {
        cacheParam: bustCache ? `_t=${Date.now()}` : 'none',
        timestamp: new Date().toISOString()
      });
      
      // Add cache-busting parameter and headers for fresh data  
      const url = bustCache ? `alerts?_t=${Date.now()}&limit=100` : 'alerts?limit=100';
      console.log('🌐 [DEBUG] Making API request...', {
        url: url,
        fullURL: `${axios.defaults.baseURL}/${url}`,
        method: 'GET',
        hasAuthHeader: !!token,
        timeout: '60000ms'
      });
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 60000, // Increase to 60 seconds for large datasets
        onDownloadProgress: (progressEvent) => {
          console.log('📊 [DEBUG] Download progress:', {
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0
          });
        }
      });
      
      console.log('✅ [DEBUG] API response received:', {
        status: response.status,
        statusText: response.statusText,
        dataType: typeof response.data,
        dataLength: response.data?.length || 0,
        isArray: Array.isArray(response.data),
        firstItemId: response.data?.[0]?.id || 'N/A',
        headers: response.headers
      });
      
      // Ensure we always have a valid alerts array
      let alertsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          alertsData = response.data;
        } else if (response.data.alerts && Array.isArray(response.data.alerts)) {
          alertsData = response.data.alerts;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          alertsData = response.data.data;
        }
      }
      
      // Filter out any null/undefined alerts and ensure required properties
      const validAlerts = alertsData.filter(alert => alert && (alert.id || alert._id)).map(alert => ({
        id: alert.id || alert._id,
        type: alert.type || alert.severity || 'info',
        status: alert.status || 'active',
        message: alert.message || alert.description || 'Alert message',
        title: alert.title || alert.message,
        equipmentId: alert.equipmentId || alert.equipment_id,
        createdAt: alert.createdAt || alert.created_at || alert.timestamp || new Date().toISOString(),
        ...alert
      }));
      
      setAlerts(validAlerts);
      console.log(`📊 Loaded ${validAlerts.length} valid alerts successfully`);
    } catch (error) {
      console.error('❌ [DEBUG] Alerts fetch failed:', {
        error,
        message: error.message,
        stack: error.stack,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        requestConfig: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
          timeout: error.config?.timeout
        },
        timestamp: new Date().toISOString()
      });
      
      // Enhanced error categorization
      if (error.code === 'ECONNABORTED') {
        setError('Request timeout - server took too long to respond. Please try again.');
        console.error('🕐 [DEBUG] Request timed out after 60 seconds');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please refresh the page and try again.');
        console.error('🔐 [DEBUG] Authentication error (401)');
      } else if (error.response?.status === 403) {
        setError('Access denied. You may not have permission to view alerts.');
        console.error('🚫 [DEBUG] Authorization error (403)');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
        console.error('🔥 [DEBUG] Server error (5xx)');
      } else if (error.name === 'NetworkError' || error.message?.includes('Network')) {
        setError('Network connection failed. Please check your internet connection.');
        console.error('🌐 [DEBUG] Network error');
      } else {
        setError(`Failed to load alerts: ${error.message}`);
        console.error('⚠️ [DEBUG] Unknown error type');
      }
    } finally {
      setLoading(false);
      console.log('🏁 [DEBUG] Fetch process completed, loading set to false');
    }
  };

  // Initial load effect with debugging
  useEffect(() => {
    console.log('🚀 [DEBUG] AlertsPage component mounted/updated', {
      appUser: appUser ? { id: appUser.id, role: appUser.role } : 'NULL',
      hasAuth0Context: !!getAccessTokenSilently,
      timestamp: new Date().toISOString()
    });
    
    if (appUser) {
      console.log('👤 [DEBUG] User found, initiating alerts fetch...');
              handleFetchAlerts();
    } else {
      console.log('⏳ [DEBUG] No user found, waiting for authentication...');
    }
  }, [appUser]);

  // Component state debugging
  useEffect(() => {
    console.log('📊 [DEBUG] AlertsPage state updated:', {
      loading,
      alertsCount: alerts?.length || 0,
      filteredCount: filteredAlerts?.length || 0,
      hasError: !!error,
      errorMessage: error || 'none'
    });
  }, [loading, alerts, filteredAlerts, error]);

  const filterAlerts = () => {
    let filtered = alerts || []; // Ensure alerts is an array

    // Search filter with null checks
    if (searchTerm) {
      filtered = filtered.filter(alert => {
        if (!alert) return false; // Skip null/undefined alerts
        
        const title = alert.title || '';
        const description = alert.description || '';
        const message = alert.message || '';
        const equipmentId = alert.equipmentId || '';
        
        const searchLower = searchTerm.toLowerCase();
        return title.toLowerCase().includes(searchLower) ||
               description.toLowerCase().includes(searchLower) ||
               message.toLowerCase().includes(searchLower) ||
               equipmentId.toLowerCase().includes(searchLower);
      });
    }

    // Type filter with null checks
    if (severityFilter !== 'all') {
      filtered = filtered.filter(alert => alert && alert.type === severityFilter);
    }

    // Status filter with null checks
    if (statusFilter !== 'all') {
      const targetStatus = statusFilter === 'resolved' ? 'resolved' : 'active';
      filtered = filtered.filter(alert => alert && alert.status === targetStatus);
    }

    setFilteredAlerts(filtered);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'info':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return { 
          backgroundColor: PROCOGIA_COLORS.background, 
          color: PROCOGIA_COLORS.darkGrey, 
          borderColor: PROCOGIA_COLORS.borderGrey 
        };
      case 'warning':
        return { 
          backgroundColor: PROCOGIA_COLORS.background, 
          color: PROCOGIA_COLORS.lightGrey, 
          borderColor: PROCOGIA_COLORS.borderGrey 
        };
      case 'info':
        return { 
          backgroundColor: '#e6f7fa', 
          color: PROCOGIA_COLORS.blue, 
          borderColor: '#b3e5fc' 
        };
      default:
        return { 
          backgroundColor: '#e8f5e8', 
          color: PROCOGIA_COLORS.green, 
          borderColor: '#c8e6c9' 
        };
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown time';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return 'Invalid date';
    }
  };

  // Wrapper function that uses stored token (no hook violations)
  const handleMarkAsResolved = async (alertId) => {
    if (!alertsAuthToken) {
      console.error('❌ [DEBUG] No auth token available for marking resolved');
      return;
    }

    try {
      await markAsResolved(alertId, alertsAuthToken);
    } catch (error) {
      console.error('❌ [DEBUG] Error marking alert as resolved:', error);
    }
  };

  const markAsResolved = async (alertId, token = null) => {
    if (!alertId) {
      console.error('Cannot resolve alert: missing alert ID');
      return;
    }
    
    try {
      // Use provided token
      if (!token) {
        console.error('❌ No auth token provided to markAsResolved');
        throw new Error('Authentication token required');
      }
      
      // Try the correct endpoint first, then fallback to alternative
      try {
        await axios.put(`alerts/${alertId}/resolve`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (firstError) {
        // Fallback to patch endpoint
        await axios.patch(`alerts/${alertId}`, { status: 'resolved' }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
              handleFetchAlerts(true); // Refresh the list with cache-busting
    } catch (error) {
      console.error('Error marking alert as resolved:', error);
      // Show user-friendly error message
      alert('Failed to resolve alert. Please try again.');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Safe filtering for stats with null checks
  const safeAlerts = alerts || [];
  const criticalCount = safeAlerts.filter(a => a && a.type === 'critical' && a.status === 'active').length;
  const warningCount = safeAlerts.filter(a => a && a.type === 'warning' && a.status === 'active').length;
  const unresolvedCount = safeAlerts.filter(a => a && a.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${themeClasses.text.primary}`}>
            System Alerts
          </h1>
          <p className={`mt-2 ${themeClasses.text.secondary}`}>
            Monitor and manage equipment alerts and notifications
          </p>
        </div>
        <button
          onClick={() => handleFetchAlerts(true)}
          disabled={loading}
          className={`mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${loading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          style={{
            backgroundColor: PROCOGIA_COLORS.green,
            borderColor: PROCOGIA_COLORS.green
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
              e.target.style.borderColor = PROCOGIA_COLORS.blue;
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = PROCOGIA_COLORS.green;
              e.target.style.borderColor = PROCOGIA_COLORS.green;
            }
          }}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: PROCOGIA_COLORS.background,
            borderColor: PROCOGIA_COLORS.borderGrey
          }}
        >
          <div className="flex items-center">
            <AlertTriangle 
              className="h-8 w-8" 
              style={{ color: PROCOGIA_COLORS.darkGrey }}
            />
            <div className="ml-3">
              <p 
                className="text-sm font-medium"
                style={{ color: PROCOGIA_COLORS.darkGrey }}
              >
                Critical
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: PROCOGIA_COLORS.darkGrey }}
              >
                {criticalCount}
              </p>
            </div>
          </div>
        </div>

        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: PROCOGIA_COLORS.background,
            borderColor: PROCOGIA_COLORS.borderGrey
          }}
        >
          <div className="flex items-center">
            <AlertTriangle 
              className="h-8 w-8" 
              style={{ color: PROCOGIA_COLORS.lightGrey }}
            />
            <div className="ml-3">
              <p 
                className="text-sm font-medium"
                style={{ color: PROCOGIA_COLORS.lightGrey }}
              >
                Warning
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: PROCOGIA_COLORS.lightGrey }}
              >
                {warningCount}
              </p>
            </div>
          </div>
        </div>

        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: '#e8f5e8',
            borderColor: '#c8e6c9'
          }}
        >
          <div className="flex items-center">
            <Clock 
              className="h-8 w-8" 
              style={{ color: PROCOGIA_COLORS.green }}
            />
            <div className="ml-3">
              <p 
                className="text-sm font-medium"
                style={{ color: PROCOGIA_COLORS.green }}
              >
                Unresolved
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: PROCOGIA_COLORS.green }}
              >
                {unresolvedCount}
              </p>
            </div>
          </div>
        </div>

        <div 
          className="border rounded-lg p-4"
          style={{
            backgroundColor: '#e6f7fa',
            borderColor: '#b3e5fc'
          }}
        >
          <div className="flex items-center">
            <CheckCircle 
              className="h-8 w-8" 
              style={{ color: PROCOGIA_COLORS.blue }}
            />
            <div className="ml-3">
              <p 
                className="text-sm font-medium"
                style={{ color: PROCOGIA_COLORS.blue }}
              >
                Total
              </p>
              <p 
                className="text-2xl font-bold"
                style={{ color: PROCOGIA_COLORS.blue }}
              >
                {alerts.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${themeClasses.bg.card} rounded-lg shadow p-6`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
            />
          </div>

          {/* Severity Filter */}
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
          >
            <option value="all">All Status</option>
            <option value="unresolved">Unresolved</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.filter(alert => alert).map((alert) => (
          <div
            key={alert.id || `alert-${Math.random()}`}
            className={`border rounded-lg p-6 ${themeClasses.bg.card}`}
            style={getSeverityColor(alert.type || 'info')}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                {getSeverityIcon(alert.type || 'info')}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={getSeverityColor(alert.type || 'info')}
                    >
                      {(alert.type || 'info').toUpperCase()}
                    </span>
                    {alert.status === 'resolved' && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        RESOLVED
                      </span>
                    )}
                  </div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: PROCOGIA_COLORS.darkGrey }}
                  >
                    {alert.title || alert.message || 'Equipment Alert'}
                  </h3>
                  <div className="space-y-1">
                    {alert.equipmentId && (
                      <div className="flex items-center space-x-2">
                        <MapPin 
                          className="h-4 w-4" 
                          style={{ color: PROCOGIA_COLORS.lightGrey }}
                        />
                        <span 
                          className="text-sm"
                          style={{ color: PROCOGIA_COLORS.lightGrey }}
                        >
                          Equipment: {alert.equipmentId}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Calendar 
                        className="h-4 w-4" 
                        style={{ color: PROCOGIA_COLORS.lightGrey }}
                      />
                      <span 
                        className="text-sm"
                        style={{ color: PROCOGIA_COLORS.lightGrey }}
                      >
                        {formatDateTime(alert.createdAt || alert.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {alert.status === 'active' && alert.id && (
                <button
                  onClick={() => handleMarkAsResolved(alert.id)}
                  className="ml-4 px-3 py-1 text-white rounded transition-colors text-sm"
                  style={{
                    backgroundColor: PROCOGIA_COLORS.blue
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = PROCOGIA_COLORS.green;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
                  }}
                >
                  Mark Resolved
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredAlerts.length === 0 && (
        <div className={`text-center py-12 ${themeClasses.bg.card} rounded-lg`}>
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className={`mt-2 text-sm font-medium ${themeClasses.text.primary}`}>No alerts found</h3>
          <p className={`mt-1 text-sm ${themeClasses.text.secondary}`}>
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default AlertsPage; 